/**
 * Owner-only Clients list — every client_account in the agency, with the
 * rollup signals that matter for "how is my business doing":
 *
 *   - Stripe status   (active / past_due / cancelled / …)
 *   - has_linked_user (have they signed in at all?)
 *   - has_active_extension_token (is the Chrome extension connected?)
 *   - last_lead_at    (when did GroupPulse last capture a lead for them?)
 *
 * Per-row action: "New setup link" — reuses the existing
 * POST /admin/extensions/setup-link endpoint, which revokes any prior
 * extension tokens and returns a fresh 15-min URL the owner can email.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { AppShell } from '../components/AppShell';
import { useApi, ApiError } from '../lib/api';

type ClientRow = {
  id: string;
  name: string;
  contact_email: string;
  plan_tier: 'standard' | 'premium' | null;
  stripe_status: string | null;
  stripe_subscription_id: string | null;
  created_at: number;
  deactivated_at: number | null;
  deletion_scheduled_at: number | null;
  has_linked_user: 0 | 1;
  has_active_extension_token: 0 | 1;
  last_lead_at: number | null;
  total_leads: number;
};

type ClientsResponse = { clients: ClientRow[] };

type SetupLinkResponse = {
  setup_url: string;
  token: string;
  expires_in_seconds: number;
};

export function Clients() {
  const { request } = useApi();

  const [clients, setClients] = useState<ClientRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Per-row "new setup link" state: clientId -> { url, copied }
  const [setupLinks, setSetupLinks] = useState<
    Record<string, { url: string; copied: boolean; loading?: boolean; error?: string }>
  >({});

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const data = await request<ClientsResponse>('/admin/clients');
      setClients(data.clients);
    } catch (e) {
      if (e instanceof ApiError) {
        const body = e.body as { error?: string } | null;
        setError(
          body?.error === 'forbidden'
            ? "You don't have access to this page."
            : `${e.status}: ${(e as Error).message}`,
        );
      } else {
        setError((e as Error).message);
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function generateSetupLink(clientId: string) {
    setSetupLinks((s) => ({ ...s, [clientId]: { url: '', copied: false, loading: true } }));
    try {
      const res = await request<SetupLinkResponse>(
        '/admin/extensions/setup-link',
        {
          method: 'POST',
          body: JSON.stringify({ client_account_id: clientId }),
        },
      );
      setSetupLinks((s) => ({
        ...s,
        [clientId]: { url: res.setup_url, copied: false },
      }));
    } catch (e) {
      setSetupLinks((s) => ({
        ...s,
        [clientId]: { url: '', copied: false, error: (e as Error).message },
      }));
    }
  }

  async function copySetupLink(clientId: string) {
    const link = setupLinks[clientId];
    if (!link?.url) return;
    try {
      await navigator.clipboard.writeText(link.url);
      setSetupLinks((s) => ({ ...s, [clientId]: { ...link, copied: true } }));
      window.setTimeout(() => {
        setSetupLinks((s) =>
          s[clientId]?.url === link.url
            ? { ...s, [clientId]: { ...link, copied: false } }
            : s,
        );
      }, 2000);
    } catch {
      /* selectable fallback */
    }
  }

  return (
    <AppShell userRole="owner">
      <main className="page">
        <div className="page-hero">
          <div className="page-hero-icon" aria-hidden="true">👥</div>
          <div className="page-hero-text">
            <h1 className="page-hero-title">Clients</h1>
            <p className="page-hero-sub">
              Everyone in the agency with their current signals — status,
              extension health, last activity.
            </p>
          </div>
        </div>
        <section className="card">
          <h1 style={{ marginBottom: 4 }}>Clients</h1>
          <p className="muted" style={{ marginTop: 0 }}>
            Every client in the agency with their current signals. Click a row
            to expand options.
          </p>

          {loading ? (
            <p className="muted">Loading…</p>
          ) : error ? (
            <div>
              <p style={errorStyle}>{error}</p>
              <button className="btn btn-secondary" onClick={refresh}>
                Retry
              </button>
            </div>
          ) : !clients || clients.length === 0 ? (
            <p className="muted">
              No clients yet.{' '}
              <Link to="/admin/new-client">Generate a checkout link</Link> to
              onboard your first one.
            </p>
          ) : (
            <div style={{ overflowX: 'auto', marginTop: 16 }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Client</th>
                    <th style={thStyle}>Plan</th>
                    <th style={thStyle}>Stripe</th>
                    <th style={thStyle}>Signed in?</th>
                    <th style={thStyle}>Extension</th>
                    <th style={thStyle}>Leads</th>
                    <th style={thStyle}>Last activity</th>
                    <th style={thStyle}></th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map((c) => {
                    const link = setupLinks[c.id];
                    return (
                      <tr key={c.id} style={trStyle}>
                        <td style={tdStyle}>
                          <Link
                            to={`/admin/clients/${c.id}`}
                            style={{ fontWeight: 500, color: 'var(--text)' }}
                          >
                            {c.name}
                          </Link>
                          <div className="muted" style={{ fontSize: 12 }}>
                            {c.contact_email}
                          </div>
                        </td>
                        <td style={tdStyle}>
                          {c.plan_tier === 'premium' ? (
                            <Pill color="purple">Premium</Pill>
                          ) : c.plan_tier === 'standard' ? (
                            <Pill color="indigo">Standard</Pill>
                          ) : (
                            <span className="muted">—</span>
                          )}
                        </td>
                        <td style={tdStyle}>
                          <StripePill status={c.stripe_status} />
                        </td>
                        <td style={tdStyle}>
                          {c.has_linked_user ? (
                            <Pill color="green">Yes</Pill>
                          ) : (
                            <Pill color="gray">No</Pill>
                          )}
                        </td>
                        <td style={tdStyle}>
                          {c.has_active_extension_token ? (
                            <Pill color="green">Connected</Pill>
                          ) : (
                            <Pill color="gray">Not yet</Pill>
                          )}
                        </td>
                        <td style={tdStyle}>{c.total_leads.toLocaleString()}</td>
                        <td style={tdStyle} className="muted">
                          {c.last_lead_at
                            ? formatRelative(c.last_lead_at)
                            : 'never'}
                        </td>
                        <td style={tdStyle}>
                          {link?.url ? (
                            <div>
                              <button
                                className="btn btn-secondary"
                                onClick={() => copySetupLink(c.id)}
                                style={{ fontSize: 13, padding: '6px 10px' }}
                              >
                                {link.copied ? 'Copied ✓' : 'Copy new link'}
                              </button>
                            </div>
                          ) : (
                            <button
                              className="btn btn-secondary"
                              onClick={() => generateSetupLink(c.id)}
                              disabled={link?.loading}
                              style={{ fontSize: 13, padding: '6px 10px' }}
                            >
                              {link?.loading ? '…' : 'New setup link'}
                            </button>
                          )}
                          {link?.error && (
                            <div
                              className="muted"
                              style={{ fontSize: 12, color: '#b91c1c', marginTop: 4 }}
                            >
                              {link.error}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </AppShell>
  );
}

// -- Bits and pieces ----------------------------------------------------------

function Pill({
  color,
  children,
}: {
  color: 'green' | 'gray' | 'red' | 'yellow' | 'indigo' | 'purple';
  children: React.ReactNode;
}) {
  const palette: Record<string, { bg: string; fg: string }> = {
    green: { bg: '#d1fae5', fg: '#065f46' },
    gray: { bg: '#f1f5f9', fg: '#475569' },
    red: { bg: '#fee2e2', fg: '#991b1b' },
    yellow: { bg: '#fef3c7', fg: '#92400e' },
    indigo: { bg: '#e0e7ff', fg: '#3730a3' },
    purple: { bg: '#ede9fe', fg: '#5b21b6' },
  };
  const c = palette[color]!;
  return (
    <span
      style={{
        display: 'inline-block',
        background: c.bg,
        color: c.fg,
        padding: '2px 8px',
        borderRadius: 12,
        fontSize: 12,
        fontWeight: 500,
      }}
    >
      {children}
    </span>
  );
}

function StripePill({ status }: { status: string | null }) {
  if (!status) return <span className="muted">—</span>;
  if (status === 'comp') {
    return <Pill color="purple">comp · free</Pill>;
  }
  if (status === 'active' || status === 'trialing') {
    return <Pill color="green">{status}</Pill>;
  }
  if (status === 'past_due' || status === 'unpaid' || status === 'incomplete') {
    return <Pill color="yellow">{status}</Pill>;
  }
  if (status === 'cancelled' || status === 'canceled' || status === 'incomplete_expired') {
    return <Pill color="red">{status}</Pill>;
  }
  return <Pill color="gray">{status}</Pill>;
}

function formatRelative(ms: number): string {
  const diff = Date.now() - ms;
  if (diff < 60_000) return 'just now';
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86400_000) return `${Math.floor(diff / 3600_000)}h ago`;
  if (diff < 30 * 86400_000) return `${Math.floor(diff / 86400_000)}d ago`;
  return new Date(ms).toLocaleDateString();
}

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 14,
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '10px 12px',
  borderBottom: '1px solid var(--border)',
  color: 'var(--muted)',
  fontWeight: 500,
  fontSize: 12,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  whiteSpace: 'nowrap',
};

const trStyle: React.CSSProperties = {
  borderBottom: '1px solid var(--border)',
};

const tdStyle: React.CSSProperties = {
  padding: '12px',
  verticalAlign: 'top',
};

const errorStyle: React.CSSProperties = {
  color: '#b91c1c',
  background: '#fef2f2',
  border: '1px solid #fecaca',
  borderRadius: 8,
  padding: '10px 12px',
  fontSize: 14,
  marginBottom: 12,
};
